import { motion } from 'motion/react'
import SelectCustom, { type SelectOption } from '../../../components/SelectCustom'
import { customerMotionTransition } from '../customer-area'
import type { OrderPageSizeOption } from './ordersListShared'
import type { TranslateFn } from './ordersListSectionTypes'

type OrdersResultsPaginationFooterProps = {
  reducedMotion: boolean
  currentPage: number
  totalPages: number
  visiblePages: number[]
  pageSize: OrderPageSizeOption
  pageSizeOptions: SelectOption[]
  pagination: { from: number; to: number }
  totalItems: number
  onSetPageSize: (value: OrderPageSizeOption) => void
  onSetCurrentPage: (page: number) => void
  t: TranslateFn
}

export const OrdersResultsPaginationFooter = ({
  reducedMotion,
  currentPage,
  totalPages,
  visiblePages,
  pageSize,
  pageSizeOptions,
  pagination,
  totalItems,
  onSetPageSize,
  onSetCurrentPage,
  t,
}: OrdersResultsPaginationFooterProps) => (
  <footer className="ca-theme-transition flex flex-wrap items-center justify-between gap-3 border-t border-(--color-border-default) bg-(--color-bg-card) px-4 py-4 sm:px-5">
    <div className="flex flex-wrap items-center gap-2 text-xs text-(--color-text-secondary)">
      <label className="inline-flex items-center gap-2 font-medium">
        <span>{t('customer.orders.pagination.perPage')}</span>
        <SelectCustom
          value={String(pageSize)}
          onChange={(value) => onSetPageSize(Number(value) as OrderPageSizeOption)}
          ariaLabel={t('customer.orders.pagination.perPage')}
          options={pageSizeOptions}
          size="sm"
          buttonClassName="h-8 min-w-[8.5rem] rounded-[10px] border-(--color-border-strong) bg-(--color-bg-card) px-2 text-xs font-semibold text-(--color-text-secondary) hover:text-(--color-text-primary)"
          optionsClassName="min-w-[8.5rem]"
        />
      </label>
      <span>
        {t('customer.orders.pagination.summary', {
          from: pagination.from,
          to: pagination.to,
          total: totalItems,
        })}
      </span>
    </div>

    <div className="flex items-center gap-1">
      <motion.button
        type="button"
        disabled={currentPage <= 1}
        onClick={() => onSetCurrentPage(Math.max(1, currentPage - 1))}
        whileTap={reducedMotion || currentPage <= 1 ? undefined : { scale: 0.985 }}
        transition={customerMotionTransition}
        className="ca-btn-secondary inline-flex h-8 items-center rounded-[10px] px-2.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t('customer.orders.pagination.previous')}
      </motion.button>

      {visiblePages.map((page) => (
        <motion.button
          key={page}
          type="button"
          onClick={() => onSetCurrentPage(page)}
          aria-current={page === currentPage ? 'page' : undefined}
          whileTap={reducedMotion ? undefined : { scale: 0.985 }}
          transition={customerMotionTransition}
          className={`inline-flex h-8 min-w-8 items-center justify-center rounded-[10px] border px-2 text-xs font-semibold transition duration-200 ${
            page === currentPage
              ? 'ca-btn-primary shadow-(--shadow-sm)'
              : 'ca-btn-secondary'
          }`}
        >
          {page}
        </motion.button>
      ))}

      <motion.button
        type="button"
        disabled={currentPage >= totalPages}
        onClick={() => onSetCurrentPage(Math.min(totalPages, currentPage + 1))}
        whileTap={reducedMotion || currentPage >= totalPages ? undefined : { scale: 0.985 }}
        transition={customerMotionTransition}
        className="ca-btn-secondary inline-flex h-8 items-center rounded-[10px] px-2.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t('customer.orders.pagination.next')}
      </motion.button>
    </div>
  </footer>
)
